<script setup>
import { KNOLIA } from "@/knolia";
import { onMounted, ref, defineEmits, defineProps, computed, unref } from "vue";
import Comments from "@/components/Comments";
import validationHistoryList from "@/components/ValidationHistoryList.vue";
import BaseSelect from "./BaseSelect.vue";
import { Gems } from "@/gems";
import { useUploadDocs } from '../composables/useUploadDocs'
import { useUserStore } from '@/store/modules/user.js'
import { MESSAGES } from "../messages";
import { useValidateSkills } from "@/composables/useValidateSkills"
import { useValidationListView } from '@/composables/useValidationListView'

const { allowedExtensions, newFileName, DownloadFile, Upload, RemoveFile, RenameFile, AssociateFileToRatingSkill } = useUploadDocs()

let emits = defineEmits(["control-modal", "set-validation", "refresh-list", "change-tab", "comments-read", "documents-read"]);
let props = defineProps({
  element: { type: Object, required: true },
  validateSkills: { type: Boolean, default: false },
  showChat: { type: Boolean, default: false },
  ratingSkillsModal: { type: Object, required: true },
  ratingStore: { type: Object, required: true },
  validationListView: { type: Object, required: true },
});
let arrComments = ref([]);
let filesList = ref({general: [], associated: []});
let loggedUserName = ref('')

const isLowerThanMobile = ref(KNOLIA.updateIsLowerThanMobile())
const isMobile = ref(KNOLIA.updateIsMobile())
const userStore = useUserStore()
const newMessagesCount = ref(0)
const newOnlyValidatorsMessagesCount = ref(0)
const validate = useValidateSkills()
const { getHistory, VALIDATION_LABEL } = useValidationListView({
  validateData: validate,
  ratingSkillsModal: props.ratingSkillsModal,
})

async function GetContent() {
  const input = document.getElementById('input')
  if(input)
    input.value = ""

  await KNOLIA.CallBackEnd("get", "get-rating-files", {
    params: {
      skills_ratings_id: props.element["skills_ratings_id"],
      requested_user: props.element["user_id"],
    },
  }).then((res) => {
    filesList.value = res.data;
  });
}

async function GetComments() {
  await KNOLIA.CallBackEnd("get", "get-rating-comments", {
    params: {
      skills_ratings_id: props.element["skills_ratings_id"],
      requested_user: props.element["user_id"],
      only_validators: props.ratingSkillsModal.ShowChatValidators
    },
  }).then((res) => {
    arrComments.value = res.data.comments
    userStore.SetCountMessages(res.data.count_messages)
  });
}

function GoToDocs(toAssociate = false) {
  if(newOnlyValidatorsMessagesCount.value != props.element.has_unread_messages_only_validators)
    newOnlyValidatorsMessagesCount.value = props.element.has_unread_messages_only_validators

  if(newMessagesCount.value != props.element.has_unread_messages)
    newMessagesCount.value = props.element.has_unread_messages

  if(toAssociate)
    props.ratingSkillsModal.SetAssociateDocs()
  else
    props.ratingSkillsModal.SetShowDocs()
}

async function GoToMessages(htmlElement, showOnlyValidatorsComments = false) {
  if(showOnlyValidatorsComments)  {
    props.ratingSkillsModal.SetShowChatValidators()
    if(newMessagesCount.value != props.element.has_unread_messages)
      newMessagesCount.value = props.element.has_unread_messages
  } else {
    props.ratingSkillsModal.SetShowChat()
    if(newOnlyValidatorsMessagesCount.value != props.element.has_unread_messages_only_validators)
      newOnlyValidatorsMessagesCount.value = props.element.has_unread_messages_only_validators
  }

  if(htmlElement.classList.contains('eye-notification')) {
    emits('comments-read', {skills_ratings_id: props.element.skills_ratings_id, only_validators: showOnlyValidatorsComments})
    htmlElement.classList.remove('eye-notification')
  }

  await GetComments()
}

async function GoToValidationHistory() {
  if(newOnlyValidatorsMessagesCount.value != props.element.has_unread_messages_only_validators)
    newOnlyValidatorsMessagesCount.value = props.element.has_unread_messages_only_validators

  if(newMessagesCount.value != props.element.has_unread_messages)
    newMessagesCount.value = props.element.has_unread_messages

  props.ratingSkillsModal.SetShowValidationHistory()
  // getHistory(props.element, true)
}

function RatingDesig(element) {
  return element.is_op_skill ? props.ratingStore.tooltipSkillOp.value[element.rating] : props.ratingStore.tooltipKnowledge.value[element.rating]
}

function HasNewMessages(element, only_validators = false) {
  if(only_validators) {
    if(element.has_unread_messages_only_validators > 0)
      return true
  } else {
    if(element.has_unread_messages > 0)
      return true
  }

  return false
}

function ManageAssociateFile(file) {
  AssociateFileToRatingSkill(file.id, props.element, file.association_id, async (newSkillsRatingsId) => { 
    await props.ratingStore.GetRatingSkill(newSkillsRatingsId) 
    GetContent() 
  })
}

onMounted(async () => {
  emits('documents-read', {skills_ratings_id: props.element.skills_ratings_id})
  GetContent()
  
  if(props.validateSkills)
    getHistory(props.element, true)

  if(props.element.validation != null)
    validation.value = props.element.validation

  newMessagesCount.value = unref(props.element.has_unread_messages)
  newOnlyValidatorsMessagesCount.value = unref(props.element.has_unread_messages_only_validators)
});

const validation  = computed({
  get() {
    return props.element.validation
  },
  set(newValue) {
    Object.assign(props.element, {validation: newValue})
  }
})


window.addEventListener('resize', () => {
  isMobile.value = KNOLIA.updateIsMobile()
  isLowerThanMobile.value = KNOLIA.updateIsLowerThanMobile()
})

</script>

<template>
  <div class="div-modal">
    <div class="div-modal-content">
      <div class="modal-header">
        <div class="div-modal-title-container">
          <span v-if="validateSkills" class="line-clamp line-clamp-1 span-user-name" :class="{'modal-title': !isMobile && !isLowerThanMobile}">{{ element.user_name }}</span>
          <span class="line-clamp line-clamp-1" :class="{'modal-desig-title': !isMobile && !isLowerThanMobile, 'modal-desig-title-validator': validateSkills}"
            @mouseenter="Gems.TooltipIfNeeded($event.target)">
            {{ element.designation }}
          </span>
          <span v-if="validateSkills">
            <span v-if="isMobile || isLowerThanMobile">Classificação: {{ element.rating }} <br> ({{ RatingDesig(element) }})</span>
            <span v-else>Classificação: {{ element.rating }} ({{ RatingDesig(element) }})</span>
          </span>
          <span v-if="validateSkills && element.isHistoryItem" class="modal-validator-info">
            Validador: {{ element.validator_name }}
          </span>
        </div>

        <div class="div-validation select-files-btn">
          <i class="fas fa-times close-modal cursor-pointer" @mouseenter="Gems.Tooltip($event.target, 'Fechar', 'tooltip')" @click="props.ratingSkillsModal.CloseModal()"></i>
          <base-select class="ddl-alt-padding" :class="{'cursor-pointer' : element.validation_date == null, 'ddl-alt-padding mobile-ddl': isMobile || isLowerThanMobile}"
            v-model="validation" @change="emits('set-validation', element)" :disabled="element.validation_date != null" v-if="validateSkills && element.without_validator != 1" >
            <option value="">--</option>
            <option :value="3" title="Registar conhecimento do pedido e impedir envio de novos e-mails de alerta diários">Em análise</option>
            <option :value="0">Invalidado</option>
            <option :value="1">Validado</option>
            <option :value="2">Abster-me</option>
            <option :value="4" title="Enviar para o administrador encontrar um novo validador" v-if="!element.without_validator">Transferir</option>
          </base-select>
        </div>
      </div>
      <div class="tabs">
        <div class="tab" :class="{'tab-active': props.ratingSkillsModal.ShowDocs}" @click="GoToDocs()">Ficheiros associados</div>
        <div class="tab" :class="{'tab-active': props.ratingSkillsModal.AssociateDocs}" v-if="!validateSkills" @click="GoToDocs(true)">Associar ficheiros</div>
        <div class="tab" :class="{'tab-active': props.ratingSkillsModal.ShowChat, 'eye-notification': HasNewMessages(element)}" 
          @click="GoToMessages($event.target)" @mouseenter="Gems.Tooltip($event.target, 'Separador de mensagens entre requerente e avaliador(es)', 'tooltip')">
          {{ validateSkills ? `Requerente` : `Comentários`}} {{ newMessagesCount > 0 ? ` [${newMessagesCount}]` : '' }}
        </div>
        <div class="tab" :class="{'tab-active': props.ratingSkillsModal.ShowChatValidators, 'eye-notification': HasNewMessages(element, true)}" v-if="validateSkills" 
          @click="GoToMessages($event.target, true)" @mouseenter="Gems.Tooltip($event.target, 'Separador de mensagens somente entre avaliadores', 'tooltip')">
          {{ 'Validadores' }} {{ newOnlyValidatorsMessagesCount > 0 ? ` [${newOnlyValidatorsMessagesCount}]` : '' }}
        </div>
        <div class="tab" :class="{'tab-active': props.ratingSkillsModal.ShowValidationHistory}" v-if="validateSkills" @click="GoToValidationHistory()">Histórico de Validação</div>
      </div>

      <div class="tab-content" v-if="props.ratingSkillsModal.ShowDocs">
        <div class="modal-body">
          <div class="div-no-docs" v-show="filesList.associated.length == 0"> Nenhum documento </div>

          <div v-for="file in filesList.associated" :key="file" class="file">
            <div class="file-name-row">
              <i class="fas fa-file"></i>

              <p class="file-name" @click="DownloadFile(file)" @mouseenter="Gems.Tooltip($event.target, 'Download', 'tooltip')">
                {{ file.designation }}
              </p>
            </div>
            <p class="file-icons-row span-date-icons">
              <span> {{ file.associated_at }} </span>
            </p>
          </div>
        </div>
      </div>

      <div class="tab-content" v-if="props.ratingSkillsModal.AssociateDocs">
        <div class="import-div" v-if="!validateSkills && props.ratingSkillsModal.AssociateDocs">
          <input class="hidden-input" id="input" type="file" @change="Upload(filesList.general, $event.target.files, () => { GetContent()}, false)" multiple />
          <button class="select-files-btn ddl-alt-padding" onclick="document.getElementById('input').click()" @mouseenter="Gems.Tooltip($event.target, allowedExtensions, 'tooltip')">
            UPLOAD
          </button>
        </div>

        <div class="modal-body" @paste="Upload(filesList.general, $event.clipboardData.files, () => { GetContent()}, false)">
          <div class="div-no-docs" v-show="filesList.general.length == 0 && Object.keys(filesList.cv).length == 0"> Nenhum documento </div>

          <div class="file cv-row" v-if="Object.keys(filesList.cv).length > 0">
            <div class="file-name-row">
              <input type="checkbox" class="cursor-pointer" v-if="!element.waiting_validation || element.waiting_validation && !filesList.cv.associatedToRatingSkill" @mouseenter="Gems.Tooltip($event.target, `${filesList.cv.associatedToRatingSkill ? 'Desassociar' : 'Associar'} a competência/conhecimento`, 'tooltip')"
                v-model="filesList.cv.associatedToRatingSkill" @change="ManageAssociateFile(filesList.cv)"/>
              <input type="checkbox" class="unavailable" v-else @mouseenter="Gems.Tooltip($event.target, MESSAGES.TOOLTIPS.UNAVAILABLE_DURING_VALIDATION, 'tooltip')" :checked="filesList.cv.associatedToRatingSkill" @click.prevent="" />

              <p class="file-name" @click="DownloadFile(filesList.cv)" @mouseenter="Gems.Tooltip($event.target, 'Download', 'tooltip')">
                Curriculum Vitae
              </p>
            </div>
            <p class="file-icons-row span-date-icons">
              <span> {{ filesList.cv.uploaded_at }} </span>
              <span class="span-icons" v-if="KNOLIA.GetRequestedUserId() == KNOLIA.GetLoggedUserId()">
                <i class="fas fa-trash remove" v-if="!filesList.cv.in_validation_process" @mouseenter="Gems.Tooltip($event.target, 'Remover', 'tooltip')" @click="RemoveFile(filesList.cv, () => { GetContent() })"></i>
                <i class="fas fa-trash remove unavailable" v-else @mouseenter="Gems.Tooltip($event.target, MESSAGES.TOOLTIPS.UNAVAILABLE_DURING_VALIDATION, 'tooltip')"></i>
              </span>
            </p>
          </div>

          <div v-for="file in filesList.general" :key="file" class="file">
            <div class="file-name-row">
              <input type="checkbox" class="cursor-pointer" v-if="!element.waiting_validation || element.waiting_validation && !file.associatedToRatingSkill" 
                @mouseenter="Gems.Tooltip($event.target, `${file.associatedToRatingSkill ? 'Desassociar' : 'Associar'} a competência/conhecimento`, 'tooltip')"
                v-model="file.associatedToRatingSkill" @change="ManageAssociateFile(file)"/>
              <input type="checkbox" class="unavailable" v-else @mouseenter="Gems.Tooltip($event.target, MESSAGES.TOOLTIPS.UNAVAILABLE_DURING_VALIDATION, 'tooltip')" :checked="file.associatedToRatingSkill" @click.prevent="" />

              <p v-if="typeof file == 'object' && !file.rename" class="file-name" @click="DownloadFile(file)" @mouseenter="Gems.Tooltip($event.target, 'Download', 'tooltip')">
                {{ file.designation }}
              </p>
              <input class="input-rename ddl-alt-padding" type="text" v-model="newFileName" @paste.stop="" v-else-if="file.rename">
            </div>
            <p class="file-icons-row span-date-icons">
              <span> {{ file.uploaded_at }} </span>
              <span class="span-icons" v-if="KNOLIA.GetRequestedUserId() == KNOLIA.GetLoggedUserId() && !file.is_cv">
                <div class="div-icon-container">
                  <i class="fas fa-spell-check remove" :class="{'selected-icon': file.rename}" v-if="!file.in_validation_process" @mouseenter="Gems.Tooltip($event.target, 'Renomear', 'tooltip')" @click="RenameFile(filesList.general, file, () => { GetContent()} )"></i>
                    <i class="fas fa-spell-check unavailable" v-else @mouseenter="Gems.Tooltip($event.target, MESSAGES.TOOLTIPS.UNAVAILABLE_DURING_VALIDATION, 'tooltip')"></i>
                </div>

                <div class="div-icon-container">
                  <i class="fas fa-trash remove" v-if="!file.in_validation_process" @mouseenter="Gems.Tooltip($event.target, 'Remover', 'tooltip')" @click="RemoveFile(file, () => { GetContent() })"></i>
                  <i class="fas fa-trash unavailable" v-else @mouseenter="Gems.Tooltip($event.target, MESSAGES.TOOLTIPS.UNAVAILABLE_DURING_VALIDATION, 'tooltip')"></i>
                </div>
              </span>
            </p>
          </div>
        </div>
      </div>

      <div class="tab-content" v-if="props.ratingSkillsModal.ShowChat || props.ratingSkillsModal.ShowChatValidators">
        <Comments
          v-model:comments="arrComments"
          :element="props.element"
          :mobile-view="isMobile || isLowerThanMobile"
          :validate-skills="validateSkills"
          :rating-skills-modal="props.ratingSkillsModal"
          :logged-user-name="loggedUserName"
          @get-comments="GetComments()"
        />
      </div>

      <div class="tab-content scrollbar" v-if="props.ratingSkillsModal.ShowValidationHistory">
        <validationHistoryList :elem="props.element" :rating-data="props.ratingStore" :validation-label="VALIDATION_LABEL" :rating-store="props.ratingStore" 
          :validate="validate"/>
      </div>
    </div>
  </div>
</template>

<style scoped>

.div-modal-content {
  background-color: var(--default-modal-color) !important;
  padding: 12px;
}

.div-modal-title-container {
  display: flex;
  gap: 5px;
  flex-direction: column;
}

.modal-title {
  text-align: start;
  margin: 0;
  font-size: 1.3em;
}

.modal-desig-title-validator {
  font-size: 1.2em !important;
  padding: 0px !important;
}

.modal-desig-title {
  font-size: 1.5em;
}

.modal-validator-info {
  font-size: 0.9em;
  color: #666;
  font-style: italic;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  padding-bottom: 5px;
  align-items: center;
}

.import-div {
  text-align: right;
  padding: 1%;
}

.div-validation {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: auto;
  align-items: stretch;
  gap: 29px;
  position: fixed;
  top: 12px;
  right: 12px;
}

.close-modal {
  align-self: flex-end;
}

.tab-content {
  background-color: var(--default-app-alt-line-color);
  height: 487px;
}

.select-files-btn {
  width: 150px;
}

.mobile-ddl {
  width: 110px;
}

.modal-body {
  padding: 10px;
  text-align: center;
}

.file {
  display: flex;
  /* justify-content: space-between;
  height: 1lh;
  padding: 5px; */
  min-height: 30px;
  margin-bottom: 5px;
  align-items: center;
}

.file p {
  margin: 0;
}

.file p i {
  margin-right: 5px;
}

.file-name-row {
  display: flex;
  align-items: center;
  flex: 4;
  gap: 5px;
}

.file-icons-row {
  flex: 2;
}

.input-rename {
  width: 96%;
}

.file-name {
  cursor: pointer;
  margin-left: 5px !important;
}

.cv-row {
  border-bottom: 1px solid;
}

.hidden-input {
  display: none;
}

.remove {
  cursor: pointer;
}

.selected-icon {
  color: #70cad1;
}

/* .fa-flag {
  margin-right: 5px;
} */

.span-date-icons {
  display: flex;
  justify-content: space-between;
}

.span-icons {
  display: flex;
  gap: 5px;
}

.div-icon-container {
  width: 12px;
  width: 14px;
}

.tabs {
  display: flex;
  text-align: center;
  align-items: center;
  flex-direction: row;
  height: auto;
  align-items: stretch;
}

.tab {
  flex: 1;
  padding: .8%;
  cursor: pointer;
  border: none;
  align-content: center;
}

.tab-active {
  background-color: var(--default-app-alt-line-color);
  border-top-right-radius: 4px;
  border-top-left-radius: 4px;
}

.close-modal {
  font-size: 1.5em;
}

.div-no-docs {
  margin-top: 10%;
}

.nofication-container {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  top: -3px;
  left: 10px;
  width: 14px !important;
  height: 14px !important;
  border-radius: 50%;
  background-color: red;
  color: white;
  font-size: 0.6em;
  text-align: center;
}

.div-comments-count {
  padding: 0;
  font-size: 7pt;
  margin: 0 auto;
  top: 0;
  bottom: 0;
}

.unavailable {
  cursor: default;
  opacity: 0.5;
}

@media screen and (max-width: 1100px) {
  .file-icons-row {
    flex: 3;
  }
}

@media screen and (max-width: 700px) {
  .div-modal-content {
    width: 100%;
    border-radius: 0px;
  }

  .span-user-name {
    font-size: 1.2em;
    font-weight: bold;
  }

  .div-validation {
    gap: 36px;
  }
}

@media screen and (max-height: 500px) {
  .div-modal-content {
    transform: translate(-50%, -33%);
  }
}

</style>
